
import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, LoaderCircle } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useGlobalState } from '../../utils/GlobalState';
import { getAuthToken, decodedToken } from '../../utils/auth';
import { acceptMuraRecord, updateRecordStatus, relabelMuraRecord } from '../../services/apiService';
import Swal from 'sweetalert2';

const MURA_TYPE_DATA = {
  pressing_light_leakage: { id: '0', color: '#FF4500' },
  dark_state_mura: { id: '1', color: '#4682B4' },
  clouding: { id: '2', color: '#9932CC' },
  wavy_mura: { id: '3', color: '#FFD700' },
  lamp_shades: { id: '4', color: '#32CD32' },
  light_bract: { id: '5', color: '#FF6347' },
  grid_mura: { id: '6', color: '#708090' },
  corner_shadow: { id: '7', color: '#FFA500' },
  bright_side_around: { id: '8', color: '#000080' },
  dark_spot: { id: '9', color: '#A52A2A' },
  bright_spot: { id: '10', color: '#9370DB' },
  horizontal_band: { id: '11', color: '#3CB371' },
  vertical_band: { id: '12', color: '#00BFFF' },
  horizontal_line: { id: '13', color: '#FF69B4' },
  vertical_line: { id: '14', color: '#A9A9A9' },
};

const getColorForType = (muraType) => {
  if (MURA_TYPE_DATA[muraType]) {
    return MURA_TYPE_DATA[muraType].color;

  }
  let hash = 0;
  for (let i = 0; i < muraType.length; i++) {
    hash = muraType.charCodeAt(i) + ((hash << 5) - hash);
  }
  let color = '#';
  for (let i = 0; i < 3; i++) {
    const value = (hash >> (i * 8)) & 0xff;
    color += ('00' + value.toString(16)).substr(-2);
  }
  return color;
};

const formatText = (text) => {
  if (!text) return 'N/A';
  return text
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const PhotoReviewOBMExpert = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { state, updateState } = useGlobalState();
  const [showRootCause, setShowRootCause] = useState(false);
  const [imageLabels, setImageLabels] = useState([]);
  const [absoluteLabels, setAbsoluteLabels] = useState([]);
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
  const [loading, setLoading] = useState(true);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState('');
  const [userType, setUserType] = useState(null);
  const [isApproved, setIsApproved] = useState(false);
  const [labeledImageLoading, setLabeledImageLoading] = useState(false);
  const [originalImageLoading, setOriginalImageLoading] = useState(true);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [activeTab, setActiveTab] = useState('');
  const [isCanvasTainted, setIsCanvasTainted] = useState(false);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [rejectionComment, setRejectionComment] = useState('');
  const canvasRef = useRef(null);
  const imgRef = useRef(null);

  const waitingForApprove = location.state?.photo || state.waitingForConfirmation || {};

  const { originalImageUrl, labeledImageUrl } = (() => {
    const dataFromStatus = waitingForApprove.dataFromStatus?.toLowerCase() || '';
    const isRelabeled = waitingForApprove.MuraRecording?.muraLabeling?.defects?.length > 0;

    if (
      dataFromStatus === 'labeling' ||
      ((dataFromStatus === 'detection_disagree' || dataFromStatus === 'detection_agree') &&
        waitingForApprove.MuraRecording?.rawImageUrl &&
        isRelabeled)
    ) {
      return {
        originalImageUrl: waitingForApprove.MuraRecording?.rawImageUrl || '',
        labeledImageUrl: waitingForApprove.MuraRecording?.labeledImageUrl || waitingForApprove.MuraRecording?.rawImageUrl || '',
      };
    } else if (dataFromStatus === 'detection_agree' || dataFromStatus === 'detection_disagree') {
      const predictedImageUrl = waitingForApprove.issueDetection?.predictions?.[0]?.predictedImageUrl?.[0] || '';
      return {
        originalImageUrl: waitingForApprove.issueDetection?.originalImageUrl || '',
        labeledImageUrl: dataFromStatus === 'detection_agree' ? predictedImageUrl : (waitingForApprove.MuraRecording?.labeledImageUrl || waitingForApprove.issueDetection?.originalImageUrl || ''),
      };
    }
    return { originalImageUrl: '', labeledImageUrl: '' };
  })();

  useEffect(() => {
    const storedActiveTab = localStorage.getItem('activeTab') || 'Accepted';
    setActiveTab(storedActiveTab);
  }, []);

  useEffect(() => {
    const token = getAuthToken();
    if (token) {
      const decoded = decodedToken(token);
      setUserType(decoded?.userType || null);
    }
  }, []);

  useEffect(() => {
    console.log('showCommentModal changed:', showCommentModal);
  }, [showCommentModal]);

  const validateDefect = (defect, index) => {
    const { center_x, center_y, width, height, muraType } = defect;
    if (
      !Number.isFinite(center_x) ||
      !Number.isFinite(center_y) ||
      !Number.isFinite(width) ||
      !Number.isFinite(height) ||
      center_x < 0 ||
      center_x > 1 ||
      center_y < 0 ||
      center_y > 1 ||
      width <= 0 ||
      width > 1 ||
      height <= 0 ||
      height > 1 ||
      !muraType
    ) {
      console.warn(`Invalid defect data at index ${index}:`, defect);
      return false;
    }
    return true;
  };

  const drawCanvas = (img, labels) => {
    const canvas = canvasRef.current;
    if (!canvas) {
      console.error('Canvas not mounted');
      return;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      console.error('Failed to get 2D context for canvas');
      return;
    }

    const container = canvas.parentElement;
    const containerWidth = container?.clientWidth || window.innerWidth;
    const containerHeight = container?.clientHeight || window.innerHeight * 0.8;
    const imgAspectRatio = img.naturalWidth / img.naturalHeight;
    let canvasWidth = containerWidth;
    let canvasHeight = containerWidth / imgAspectRatio;

    if (canvasHeight > containerHeight) {
      canvasHeight = containerHeight;
      canvasWidth = containerHeight * imgAspectRatio;
    }

    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    canvas.style.width = `${canvasWidth}px`;
    canvas.style.height = `${canvasHeight}px`;

    setImageDimensions({ width: img.naturalWidth, height: img.naturalHeight });

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, img.naturalWidth, img.naturalHeight);

    labels.forEach((defect, index) => {
      if (!validateDefect(defect, index)) return;

      const center_x = Math.max(0, Math.min(1, defect.center_x));
      const center_y = Math.max(0, Math.min(1, defect.center_y));
      const width = Math.max(0, Math.min(1, defect.width));
      const height = Math.max(0, Math.min(1, defect.height));

      const x = (center_x - width / 2) * img.naturalWidth;
      const y = (center_y - height / 2) * img.naturalHeight;
      const pixelWidth = width * img.naturalWidth;
      const pixelHeight = height * img.naturalHeight;

      const edgeThreshold = 10;
      const clampedX = Math.max(0, Math.min(x, img.naturalWidth - pixelWidth));
      const clampedY = Math.max(0, Math.min(y, img.naturalHeight - pixelHeight));
      const clampedWidth = Math.min(pixelWidth, img.naturalWidth - clampedX);
      const clampedHeight = Math.min(pixelHeight, img.naturalHeight - clampedY);

      const finalX = Math.max(edgeThreshold, Math.min(clampedX, img.naturalWidth - clampedWidth - edgeThreshold));
      const finalY = Math.max(edgeThreshold, Math.min(clampedY, img.naturalHeight - clampedHeight - edgeThreshold));
      const finalWidth = Math.min(clampedWidth, img.naturalWidth - finalX - edgeThreshold);
      const finalHeight = Math.min(clampedHeight, img.naturalHeight - finalY - edgeThreshold);

      const color = getColorForType(defect.muraType);
      ctx.strokeStyle = color;
      ctx.lineWidth = 5;
      ctx.strokeRect(finalX, finalY, finalWidth, finalHeight);

      if (defect.muraType && defect.muraType.toLowerCase() !== 'other') {
        ctx.fillStyle = color;
        ctx.font = 'bold 20px Arial';
        const label = `ID: ${defect.muraLabId || 'unknown'} | ${defect.muraType}`;
        const textMetrics = ctx.measureText(label);
        const textWidth = textMetrics.width;
        const textHeight = 16;
        const topThreshold = 20;
        const rightThreshold = 20;

        const labelY = finalY < topThreshold ? finalY + finalHeight + textHeight + 20 : finalY - 20;
        let labelX = finalX;
        if (labelX + textWidth > img.naturalWidth - rightThreshold) {
          labelX = Math.max(edgeThreshold, finalX + finalWidth - textWidth - 10);
        }

        ctx.fillText(label, labelX, labelY);
      }
    });
  };

  const fetchLabels = async () => {
    try {
      setLoading(true);
      setOriginalImageLoading(true);

      const isRelabeled = waitingForApprove.MuraRecording?.muraLabeling?.defects?.length > 0;

      if (waitingForApprove.dataFromStatus?.toLowerCase() === 'detection_agree' && !isRelabeled) {
        const predictions = waitingForApprove.issueDetection?.predictions || [];
        const absLabels = predictions.map((defect) => ({
          muraLabId: defect.issueId || 'unknown',
          center_x: defect.coordinates.x,
          center_y: defect.coordinates.y,
          width: defect.coordinates.width,
          height: defect.coordinates.height,
          muraType: defect.issueType || 'other',
        }));
        setAbsoluteLabels(absLabels);
        setLoading(false);
        setOriginalImageLoading(false);
        return;
      }

      const labelsUrl = waitingForApprove.MuraRecording?.labelFileUrl || '';
      const imageUrl = isRelabeled ? waitingForApprove.MuraRecording?.rawImageUrl || '' : waitingForApprove.issueDetection?.originalImageUrl || '';

      const [labelResponse, imageResponse] = await Promise.all([
        labelsUrl ? fetch(labelsUrl) : Promise.resolve(null),
        imageUrl ? fetch(imageUrl, { cache: 'no-cache' }) : Promise.resolve(null),
      ]);

      let parsedLabels = [];
      if (labelResponse && labelResponse.ok) {
        const text = await labelResponse.text();
        if (text.trim()) {
          parsedLabels = text
            .trim()
            .split('\n')
            .map((line, index) => {
              const [classId, center_x, center_y, width, height, ...rest] = line.split(/\s+/).map(Number);
              const muraType = Object.keys(MURA_TYPE_DATA).find(
                (key) => MURA_TYPE_DATA[key].id === classId.toString()
              ) || 'other';
              const defect = { classId, center_x, center_y, width, height, muraType };
              return validateDefect(defect, index) ? defect : null;
            })
            .filter(Boolean);
        }
      }

      if (!parsedLabels.length || isRelabeled) {
        parsedLabels = waitingForApprove.MuraRecording?.muraLabeling?.defects?.map((defect, index) => {
          const formattedDefect = {
            muraLabId: defect.muraLabId || 'unknown',
            center_x: defect.bbox?.[0] || 0.5,
            center_y: defect.bbox?.[1] || 0.5,
            width: defect.bbox?.[2] || 0.1,
            height: defect.bbox?.[3] || 0.1,
            muraType: defect.muraType || 'other',
          };
          return validateDefect(formattedDefect, index) ? formattedDefect : null;
        }).filter(Boolean) || [];
      }

      setImageLabels(parsedLabels);
      if (!parsedLabels.length) {
        Swal.fire({
          icon: 'warning',
          title: 'Warning',
          text: 'Failed to load label data. Using fallback labels.',
        });
      }
    } catch (error) {
      console.error('Error fetching labels:', error);
      setImageLabels([]);
      Swal.fire({
        icon: 'warning',
        title: 'Warning',
        text: 'Failed to load label data. Using fallback labels.',
      });
    } finally {
      setLoading(false);
      setOriginalImageLoading(false);
    }
  };

  useEffect(() => {
    if (waitingForApprove && Object.keys(waitingForApprove).length > 0) {
      fetchLabels();
    }
  }, [waitingForApprove]);

  useEffect(() => {
    if (absoluteLabels.length > 0 && imageDimensions.width > 0 && imageDimensions.height > 0) {
      const normalizedLabels = absoluteLabels.map((label) => ({
        ...label,
        center_x: label.center_x / imageDimensions.width,
        center_y: label.center_y / imageDimensions.height,
        width: label.width / imageDimensions.width,
        height: label.height / imageDimensions.height,
      }));
      setImageLabels(normalizedLabels);
    }
  }, [absoluteLabels, imageDimensions]);

  useEffect(() => {
    const isRelabeled = waitingForApprove.MuraRecording?.muraLabeling?.defects?.length > 0;
    if (waitingForApprove.dataFromStatus?.toLowerCase() === 'detection_agree' && !isRelabeled) {
      setLabeledImageLoading(false);
      return;
    }

    if (!originalImageUrl || !canvasRef.current || !imgRef.current || !imageLabels.length) {
      setLabeledImageLoading(false);
      return;
    }

    setLabeledImageLoading(true);
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    const urlWithCacheBust = `${originalImageUrl}?ts=${new Date().getTime()}`;

    img.onerror = () => {
      console.warn('CORS failed for canvas image');
      setIsCanvasTainted(true);
      img.src = `/api/image-proxy?url=${encodeURIComponent(originalImageUrl)}`;
    };

    img.onload = () => {
      drawCanvas(img, imageLabels);
      setLabeledImageLoading(false);
      try {
        canvasRef.current.toDataURL('image/png');
        setIsCanvasTainted(false);
      } catch (e) {
        console.warn('Canvas is tainted:', e);
        setIsCanvasTainted(true);
      }
    };

    img.src = urlWithCacheBust;

    const handleResize = () => {
      if (imgRef.current && canvasRef.current && imageLabels.length) {
        drawCanvas(imgRef.current, imageLabels);
        try {
          canvasRef.current.toDataURL('image/png');
          setIsCanvasTainted(false);
        } catch (e) {
          setIsCanvasTainted(true);
        }
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [originalImageUrl, imageLabels, waitingForApprove.dataFromStatus]);

  useEffect(() => {
    if (!showRootCause && waitingForApprove && Object.keys(waitingForApprove).length > 0) {
      setOriginalImageLoading(true);
      fetchLabels();
      setShowCommentModal(false);
    }
  }, [showRootCause, waitingForApprove]);

  const handleImageClick = (type) => {
    setIsLoadingPreview(true);
    if (type === 'Original Image') {
      setPreviewImageUrl(originalImageUrl);
      setIsPreviewOpen(true);
    } else if (type === 'Labeled Image') {
      if (waitingForApprove.dataFromStatus?.toLowerCase() === 'detection_agree' && !waitingForApprove.MuraRecording?.muraLabeling?.defects?.length) {
        const predictedImageUrl = waitingForApprove.issueDetection?.predictions?.[0]?.predictedImageUrl?.[0] || labeledImageUrl;
        setPreviewImageUrl(predictedImageUrl || labeledImageUrl);
        setIsPreviewOpen(true);
      } else if (canvasRef.current && !isCanvasTainted) {
        try {
          const dataUrl = canvasRef.current.toDataURL('image/png');
          setPreviewImageUrl(dataUrl);
          setIsPreviewOpen(true);
        } catch (e) {
          console.error('Failed to export canvas:', e);
          setPreviewImageUrl(labeledImageUrl);
          setIsPreviewOpen(true);
          Swal.fire({
            icon: 'warning',
            title: 'Warning',
            text: 'Unable to display labeled image preview due to security restrictions. Showing available labeled image or original image.',
          });
        }
      } else {
        setPreviewImageUrl(labeledImageUrl);
        setIsPreviewOpen(true);
        if (isCanvasTainted) {
          Swal.fire({
            icon: 'warning',
            title: 'Warning',
            text: 'Unable to display labeled image preview due to security restrictions. Showing available labeled image or original image.',
          });
        }
      }
    }
  };

  const handleAccept = async () => {
    try {
      const recordId = waitingForApprove._id;
      if (!recordId) throw new Error('No record ID found');

      const result = await Swal.fire({
        title: 'Are you sure?',
        text: 'Do you really want to accept this mura record?',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Yes, Accept it',
        cancelButtonText: 'Cancel',
        reverseButtons: true,
        confirmButtonColor: 'green',
        cancelButtonColor: 'red',
      });

      if (result.isConfirmed) {
        await acceptMuraRecord(recordId);
        setIsApproved(true);

        updateState({
          photoManagement: {
            ...state.photoManagement,
            data: state.photoManagement?.data?.map((photo) =>
              photo._id === recordId ? { ...photo, status: 'accepted' } : photo
            ) || [],
          },
          waitingForConfirmation: null,
        });

        await Swal.fire({
          icon: 'success',
          title: 'Accepted!',
          text: 'Mura record accepted successfully.',
          confirmButtonColor: '#2563eb',
          timer: 1500,
          showConfirmButton: false,
        });

        navigate('/photomanagement');
      }
    } catch (error) {
      console.error('Error accepting mura record:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Failed to accept mura record',
        confirmButtonColor: '#ef4444',
      });
    }
  };

  const handleApprove = async () => {
    try {
      const recordId = waitingForApprove._id;
      if (!recordId) throw new Error('No record ID found');

      const result = await Swal.fire({
        title: 'Are you sure?',
        text: 'Do you want to approve this record?',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Yes, Approve',
        cancelButtonText: 'Cancel',
        reverseButtons: true,
        confirmButtonColor: 'green',
        cancelButtonColor: 'red',
      });

      if (result.isConfirmed) {
        await updateRecordStatus(recordId, 'approved');
        setIsApproved(true);

        updateState({
          photoManagement: {
            ...state.photoManagement,
            data: state.photoManagement?.data?.map((photo) =>
              photo._id === recordId ? { ...photo, status: 'approved' } : photo
            ) || [],
          },
          waitingForConfirmation: null,
        });

        await Swal.fire({
          icon: 'success',
          title: 'Approved',
          text: 'The record has been approved successfully!',
          timer: 2000,
          showConfirmButton: false,
        });
        navigate('/photomanagement');
      }
    } catch (error) {
      console.error('Error approving record:', error);
      Swal.fire({
        icon: 'error',
        title: 'Approval Failed',
        text: error.message || 'Something went wrong. Please try again.',
      });
    }
  };

  const handleRejectWithComments = async () => {
    try {
      if (!rejectionComment.trim()) {
        Swal.fire({
          icon: 'warning',
          title: 'Comment Required',
          text: 'Please add a comment before rejecting.',
          confirmButtonColor: '#ef4444',
        });
        return;
      }

      const recordId = waitingForApprove?._id;
      if (!recordId) throw new Error('No record ID found');

      const result = await Swal.fire({
        title: 'Confirm Rejection',
        text: 'Are you sure you want to reject this record with your comments?',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Yes, Reject',
        cancelButtonText: 'Cancel',
        reverseButtons: true,
        confirmButtonColor: '#ef4444',
        cancelButtonColor: '#6b7280',
      });

      if (result.isConfirmed) {
        await relabelMuraRecord(recordId, rejectionComment);

        updateState({
          photoManagement: {
            ...state.photoManagement,
            data: state.photoManagement?.data?.map((photo) =>
              photo._id === recordId ? { ...photo, status: 'relabel' } : photo
            ) || [],
          },
          waitingForConfirmation: null,
        });

        setShowCommentModal(false);
        setRejectionComment('');

        Swal.fire({
          icon: 'success',
          title: 'Rejected',
          text: 'The record has been rejected and sent for relabeling.',
          timer: 2000,
          showConfirmButton: false,
        }).then(() => {
          navigate('/photomanagement');
        });
      }
    } catch (error) {
      console.error('Error rejecting with comments:', error);
      Swal.fire({
        icon: 'error',
        title: 'Rejection Failed',
        text: error.message || 'Something went wrong. Please try again.',
        confirmButtonColor: '#ef4444',
      });
    }
  };

  if (showRootCause) {
    return (
      <div className="min-h-screen bg-gray-900 flex flex-col text-white">
        <div className="flex items-center p-4 border-b border-gray-700">
          <ArrowLeft
            className="w-5 h-5 text-white cursor-pointer mr-2"
            onClick={() => {
              setShowRootCause(false);
              setShowCommentModal(false);
            }}
          />
          <h1 className="text-lg font-semibold">Root Cause Confirmation</h1>
        </div>
        <div className="flex-1 p-4 sm:p-6">
          <p className="mb-3 text-2xl font-medium">The Latest Root Cause Recording</p>
          <div className="mb-2">
            <h3 className="text-base sm:text-lg md:text-xl font-semibold mb-2">Labeling Recording</h3>
            <div className="p-2 bg-gray-800 border border-gray-600 rounded-md">
              <div className="space-y-2">
                {waitingForApprove.dataFromStatus?.toLowerCase() === 'detection_agree' ? (
                  waitingForApprove.MuraRecording?.muraLabeling?.defects?.some(
                    (defect) => defect.muraType || defect.muraLabelIssue?.muraRootCause || defect.muraLabelIssue?.muraRootIssue?.length > 0
                  ) ? (
                    [...new Set(
                      waitingForApprove.MuraRecording.muraLabeling.defects.map((defect) => {
                        const muraType = formatText(defect.muraType);
                        const rootCause = formatText(defect.muraLabelIssue?.muraRootCause);
                        const rootIssues = defect.muraLabelIssue?.muraRootIssue?.map(formatText) || [];
                        return rootIssues.length > 0
                          ? rootIssues.map((ri) => [muraType, rootCause, ri].filter(Boolean).join(' : '))
                          : [[muraType, rootCause].filter(Boolean).join(' : ')];
                      }).flat()
                    )].map((entry, idx) => (
                      <p key={idx} className="text-sm sm:text-base">{entry || 'N/A'}</p>
                    ))
                  ) : (
                    waitingForApprove.issueDetection?.predictions?.length > 0 ? (
                      [...new Map(
                        waitingForApprove.issueDetection.predictions
                          .map((pred) => [`${formatText(pred.issueType)}:${formatText(pred.rootCauseType)}`, pred])
                      ).values()].map((pred, idx) => (
                        <p key={idx} className="text-sm sm:text-base">
                          {formatText(pred.issueType)} : {formatText(pred.rootCauseType)}
                        </p>
                      ))
                    ) : (
                      <p className="text-sm sm:text-base">N/A</p>
                    )
                  )
                ) : (
                  waitingForApprove.MuraRecording?.muraLabeling?.defects?.length > 0 ? (
                    [...new Set(
                      waitingForApprove.MuraRecording.muraLabeling.defects.map((defect) => {
                        const muraType = formatText(defect.muraType);
                        const rootCause = formatText(defect.muraLabelIssue?.muraRootCause);
                        const rootIssues = defect.muraLabelIssue?.muraRootIssue?.map(formatText) || [];
                        return rootIssues.length > 0
                          ? rootIssues.map((ri) => [muraType, rootCause, ri].filter(Boolean).join(' : '))
                          : [[muraType, rootCause].filter(Boolean).join(' : ')];
                      }).flat()
                    )].map((entry, idx) => (
                      <p key={idx} className="text-sm sm:text-base">{entry || 'N/A'}</p>
                    ))
                  ) : (
                    <p className="text-sm sm:text-base">N/A</p>
                  )
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="p-4 sm:p-6 space-y-3">
          <button
            className={`w-full py-2 sm:py-3 rounded-lg font-medium text-base sm:text-base ${isApproved ? 'bg-gray-500 cursor-not-allowed' : 'bg-green-600 text-white'}`}
            onClick={handleApprove}
            disabled={isApproved}
          >
            {isApproved ? 'Approved' : 'Approve'}
          </button>
          <button
            className="w-full py-2 sm:py-3 rounded-lg font-medium text-base sm:text-base bg-red-600 text-white hover:bg-red-700"
            onClick={() => setShowCommentModal(true)}
          >
            Reject & Comments
          </button>
        </div>
        {showCommentModal && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-lg w-full max-w-md p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-white">Reject & Comments</h3>
                <button
                  onClick={() => {
                    setShowCommentModal(false);
                    setRejectionComment('');
                  }}
                  className="text-gray-400 hover:text-white text-2xl"
                >
                  &times;
                </button>
              </div>
              <p className="text-gray-300 text-sm mb-4">
                Before returning the images to the provider, please add comments
              </p>
              <textarea
                value={rejectionComment}
                onChange={(e) => setRejectionComment(e.target.value)}
                placeholder="Enter your comments here..."
                className="w-full h-32 bg-gray-700 text-white border border-gray-600 rounded-lg p-3 resize-none focus:outline-none focus:border-blue-500"
                maxLength={500}
              />
              <div className="text-right text-xs text-gray-400 mt-1 mb-4">
                {rejectionComment.length}/500 characters
              </div>

              <button
                onClick={handleRejectWithComments}
                disabled={rejectionComment.trim().length === 0}
                className={`w-full font-medium py-2 rounded-lg transition-colors duration-200
                ${rejectionComment.trim().length === 0
                    ? "bg-gray-500 text-gray-300 cursor-not-allowed"
                    : "bg-blue-500 hover:bg-blue-600 text-white"}`
                  }
              >
                Apply
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="bg-black text-white min-h-screen p-4">
      <div className="flex items-center justify-start mb-2">
        <ArrowLeft
          className="w-5 h-5 text-white cursor-pointer"
          onClick={() => navigate('/photomanagement')}
        />
        <h2 className="ml-2 text-base sm:text-lg md:text-xl">Photo Review</h2>
      </div>
      <div className="max-w-4xl mx-auto w-full">
        <div className="mb-4">
          <h3 className="text-base sm:text-lg md:text-xl font-semibold mb-2">Original Image</h3>
          <div
            className="relative w-full h-[40vh] sm:h-[50vh] md:h-[60vh] lg:h-[70vh] flex items-center justify-center border border-slate-500"
            onClick={() => handleImageClick('Original Image')}
          >
            {originalImageLoading && (
              <LoaderCircle className="animate-spin text-white w-6 h-6 sm:w-8 sm:h-8 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10" />
            )}
            {originalImageUrl ? (
              <img
                ref={imgRef}
                src={originalImageUrl}
                alt="Original"
                className="w-full h-full object-contain rounded transition-opacity duration-300"
                onLoad={() => setOriginalImageLoading(false)}
                onError={(e) => {
                  setOriginalImageLoading(false);
                  console.error('Failed to load original image:', originalImageUrl);
                  if (
                    originalImageUrl !== waitingForApprove.MuraRecording?.rawImageUrl &&
                    waitingForApprove.MuraRecording?.rawImageUrl
                  ) {
                    e.target.src = waitingForApprove.MuraRecording.rawImageUrl;
                    setOriginalImageLoading(true);
                  }
                }}
              />
            ) : (
              <p className="text-gray-400 text-sm sm:text-base p-4 text-center">No image available</p>
            )}
          </div>
        </div>

        <div className="flex flex-col mb-3 mt-3">
          {userType === 'obmExpert' && activeTab !== 'Accepted' && (
            <div className="flex justify-start mb-2">
              <button
                onClick={() => {
                  setShowRootCause(true);
                  setShowCommentModal(false);
                }}
                className="bg-blue-700 text-xs sm:text-base text-white py-1 px-3 sm:px-6 sm:py-3 rounded-full"
              >
                Root Cause Confirm
              </button>
            </div>
          )}
          {(userType === 'admin' || userType === 'powerAdmin') && activeTab !== 'Accepted' && (
            <div className="flex justify-center space-x-4">
              <button
                className={`bg-green-700 text-xs sm:text-base text-white py-1 px-3 sm:px-10 sm:py-2 rounded-lg ${isApproved ? 'bg-gray-500 cursor-not-allowed' : ''}`}
                onClick={handleAccept}
                disabled={isApproved}
              >
                {isApproved ? 'Accepted' : 'Accept'}
              </button>
              <button
                className="bg-red-700 text-xs sm:text-base text-white py-1 px-3 sm:px-10 sm:py-2 rounded-lg hover:bg-red-600"
                onClick={() => setShowCommentModal(true)}
              >
                Reject & Comments
              </button>
            </div>
          )}
        </div>
        <div className="mb-4">
          <h3 className="text-base sm:text-lg md:text-xl font-semibold mb-2">Labeled Image</h3>
          <div
            className="relative w-full h-[40vh] sm:h-[50vh] md:h-[60vh] lg:h-[70vh] flex items-center justify-center border border-slate-500"
            onClick={() => handleImageClick('Labeled Image')}
          >
            {labeledImageLoading && (
              <LoaderCircle className="animate-spin text-white w-6 h-6 sm:w-8 sm:h-8 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10" />
            )}
            {labeledImageUrl ? (
              waitingForApprove.dataFromStatus?.toLowerCase() === 'detection_agree' && !waitingForApprove.MuraRecording?.muraLabeling?.defects?.length ? (
                <img
                  src={labeledImageUrl}
                  alt="Labeled"
                  className="w-full h-full object-contain rounded transition-opacity duration-300"
                  onLoad={() => setLabeledImageLoading(false)}
                  onError={() => {
                    setLabeledImageLoading(false);
                    console.error('Failed to load labeled image:', labeledImageUrl);
                  }}
                />
              ) : (
                <canvas
                  ref={canvasRef}
                  className="w-full h-full object-contain rounded transition-opacity duration-300"
                />
              )
            ) : (
              <p className="text-gray-400 text-sm sm:text-base p-4 text-center">No image available</p>
            )}
          </div>
        </div>

        <div className="mb-4">
          <h3 className="text-base sm:text-lg md:text-xl font-semibold mb-2">Labeling Recording</h3>
          <div className="p-2 bg-gray-800 border border-gray-600 rounded-md">
            <div className="space-y-2">
              {waitingForApprove.dataFromStatus?.toLowerCase() === 'detection_agree' ? (
                waitingForApprove.MuraRecording?.muraLabeling?.defects?.some(
                  (defect) => defect.muraType || defect.muraLabelIssue?.muraRootCause || defect.muraLabelIssue?.muraRootIssue?.length > 0
                ) ? (
                  [...new Set(
                    waitingForApprove.MuraRecording.muraLabeling.defects.map((defect) => {
                      const muraType = formatText(defect.muraType);
                      const rootCause = formatText(defect.muraLabelIssue?.muraRootCause);
                      const rootIssues = defect.muraLabelIssue?.muraRootIssue?.map(formatText) || [];
                      return rootIssues.length > 0
                        ? rootIssues.map((ri) => [muraType, rootCause, ri].filter(Boolean).join(' : '))
                        : [[muraType, rootCause].filter(Boolean).join(' : ')];
                    }).flat()
                  )].map((entry, idx) => (
                    <p key={idx} className="text-sm sm:text-base">{entry || 'N/A'}</p>
                  ))
                ) : (
                  waitingForApprove.issueDetection?.predictions?.length > 0 ? (
                    [...new Map(
                      waitingForApprove.issueDetection.predictions
                        .map((pred) => [`${formatText(pred.issueType)}:${formatText(pred.rootCauseType)}`, pred])
                    ).values()].map((pred, idx) => (
                      <p key={idx} className="text-sm sm:text-base">
                        {formatText(pred.issueType)} : {formatText(pred.rootCauseType)}
                      </p>
                    ))
                  ) : (
                    <p className="text-sm sm:text-base">N/A</p>
                  )
                )
              ) : (
                waitingForApprove.MuraRecording?.muraLabeling?.defects?.length > 0 ? (
                  [...new Set(
                    waitingForApprove.MuraRecording.muraLabeling.defects.map((defect) => {
                      const muraType = formatText(defect.muraType);
                      const rootCause = formatText(defect.muraLabelIssue?.muraRootCause);
                      const rootIssues = defect.muraLabelIssue?.muraRootIssue?.map(formatText) || [];
                      return rootIssues.length > 0
                        ? rootIssues.map((ri) => [muraType, rootCause, ri].filter(Boolean).join(' : '))
                        : [[muraType, rootCause].filter(Boolean).join(' : ')];
                    }).flat()
                  )].map((entry, idx) => (
                    <p key={idx} className="text-sm sm:text-base">{entry || 'N/A'}</p>
                  ))
                ) : (
                  <p className="text-sm sm:text-base">N/A</p>
                )
              )}
            </div>
          </div>
        </div>

        {isPreviewOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="relative w-full max-w-[90vw] sm:max-w-[80vw] md:max-w-[70vw] lg:max-w-[60vw] h-[80vh] sm:h-[85vh]">
              <div className="relative w-full h-full flex items-center justify-center rounded-lg">
                {isLoadingPreview && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 z-20">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
                  </div>
                )}
                <img
                  src={previewImageUrl}
                  alt="Preview"
                  onLoad={() => setIsLoadingPreview(false)}
                  //className="w-full h-full object-contain rounded-lg"
                  className="w-[700px] h-[700px] object-contain rounded-lg"
                />
              </div>
              {!isLoadingPreview && (
                <div className="absolute top-0 left-0 w-full flex items-center justify-between bg-black bg-opacity-50 px-3 py-2 z-10">
                  <h3 className="text-sm sm:text-base md:text-lg font-medium text-white">
                    {previewImageUrl === originalImageUrl ? 'Original Image' : 'Labeled Image'}
                  </h3>
                  <button
                    className="text-white text-lg sm:text-xl md:text-2xl"
                    onClick={() => setIsPreviewOpen(false)}
                  >
                    &times;
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {showCommentModal && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-lg w-full max-w-md p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-white">Reject & Comments</h3>
                <button
                  onClick={() => {
                    setShowCommentModal(false);
                    setRejectionComment('');
                  }}
                  className="text-gray-400 hover:text-white text-2xl"
                >
                  &times;
                </button>
              </div>
              <p className="text-gray-300 text-sm mb-4">
                Before returning the images to the provider, please add comments
              </p>
              <textarea
                value={rejectionComment}
                onChange={(e) => setRejectionComment(e.target.value)}
                placeholder="Enter your comments here..."
                className="w-full h-32 bg-gray-700 text-white border border-gray-600 rounded-lg p-3 resize-none focus:outline-none focus:border-blue-500"
                maxLength={500}
              />
              <div className="text-right text-xs text-gray-400 mt-1 mb-4">
                {rejectionComment.length}/500 characters
              </div>
              <button
                onClick={handleRejectWithComments}
                disabled={rejectionComment.trim().length === 0}
                className={`w-full font-medium py-2 rounded-lg transition-colors duration-200
                  ${rejectionComment.trim().length === 0
                    ? "bg-gray-500 text-gray-300 cursor-not-allowed"
                    : "bg-blue-500 hover:bg-blue-600 text-white"}`
                  }
              >
                Apply
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PhotoReviewOBMExpert;

